//src/pages/attendance/LeavesTable.jsx
import { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { getLeavesByUserId, getLeavesByStaffId, getLeavesByDoctorId } from "../../services/leaveService";
import Refresh from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { downloadFile } from "../../services/uploadService";
import { AttachFile } from "@mui/icons-material";
import AddNewInvoice from "./ApplyLeave";
import { useSelector } from 'react-redux'; // Import useSelector
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import { getOrganizationName } from "../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../utils/patientTableExcelExport";
import {
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../utils/pageLayoutStyles";
import { getDoctorByUserId } from "../../services/doctorService"; // Import the service
import { getStaffByUserId } from "../../services/StaffService"; // Import the service

const columns = [
  // { id: "attendanceId", label: "Leave Id" },
  { id: "user", label: "User" },
  { id: "leaveFromDate", label: "From Date" },
  { id: "leaveToDate", label: "To Date" },
  { id: "leaveType", label: "Leave Type" },
  { id: "leaveReason", label: "Leave Reason" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const Popup = ({ message, onClose }) => (
  <div style={popupStyles.overlay}>
    <div style={popupStyles.popup}>
      <p>{message}</p>
      <button onClick={onClose} style={popupStyles.closeButton}>Close</button>
    </div>
  </div>
);

const popupStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: '140px',
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.011)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  popup: {
    backgroundColor: '#fff',
    padding: '30px 40px',
    borderRadius: '8px',
    width: '300px',
    textAlign: 'center',
  },
  closeButton: {
    marginTop: '10px',
    padding: '8px 16px',
    backgroundColor: '#007BFF',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};

export default function AttendanceTable() {
  const [invoice_list, setInvoiceList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ invoice_list: true });
  const user = useSelector((state) => state.user.user); // Access user from Redux
  const [responseMessage, setResponseMessage] = useState('');
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [invoice_info, setInvoiceInfo] = useState({});
  const [open_invoice_modal, setOpenInvoiceModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [leaveFromDate, setleaveFromDate] = useState("");
  const [leaveToDate, setleaveToDate] = useState("");
  const [selectedLeaveType, setSelectedLeaveType] = useState("");

  useEffect(() => {
    fetchInvoice();
  }, []);

  const fetchInvoice = async () => {
    setFetching({ ...fetching, invoice_list: true });
    try {
      // Fetch user details (staff/doctor) to get the correct ID for querying leaves
      // This matches how ApplyLeave.jsx creates leaves (using staffId/doctorId, not userId)
      let userDetails;
      let data;
      
      if (user.roleName === "doctor" || user.roleName === "surgeon") {
        userDetails = await getDoctorByUserId(user.id); // Fetch doctor details
        if (userDetails?.id) {
          // Try to get leaves using doctor-specific endpoint first
          try {
            data = await getLeavesByDoctorId(userDetails.id);
          } catch (err) {
            // If doctor endpoint doesn't exist, try with doctor ID as userId
            console.warn("Doctor endpoint not available, trying with doctor ID as userId:", err);
            try {
              data = await getLeavesByUserId(userDetails.id);
            } catch (err2) {
              // Final fallback: try with userId
              console.warn("Failed with doctor ID, trying with userId:", err2);
              data = await getLeavesByUserId(user.id);
            }
          }
        } else {
          // Fallback to userId if doctor details not found
          data = await getLeavesByUserId(user.id);
        }
      } else {
        userDetails = await getStaffByUserId(user.id); // Fetch staff details
        if (userDetails?.id) {
          // Try to get leaves using staff-specific endpoint first
          try {
            data = await getLeavesByStaffId(userDetails.id);
          } catch (err) {
            // If staff endpoint doesn't exist, try with staff ID as userId
            console.warn("Staff endpoint not available, trying with staff ID as userId:", err);
            try {
              data = await getLeavesByUserId(userDetails.id);
            } catch (err2) {
              // Final fallback: try with userId
              console.warn("Failed with staff ID, trying with userId:", err2);
              data = await getLeavesByUserId(user.id);
            }
          }
        } else {
          // Fallback to userId if staff details not found
          data = await getLeavesByUserId(user.id);
        }
      }
      
      // Filter the data to only include records with status "ON_LEAVE"
      const filteredData = data.filter(item => item.status === "ON_LEAVE");
      // Sort the filtered data by attendanceId in descending order
      const sortedData = filteredData.sort((a, b) => b.attendanceId - a.attendanceId);
      setInvoiceList(sortedData);

    } catch (error) {
      console.error("Error fetching leaves:", error);
      setInvoiceList([]); // Set empty array on error
    }
    setFetching({ ...fetching, invoice_list: false });
  };

  const filteredRows = invoice_list.filter((item) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const vals = [user?.username, item.leaveFromDate, item.leaveToDate, item.leaveType, item.leaveReason]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!vals.some((v) => v.includes(q))) return false;
    }
    if (leaveFromDate) {
      if (!dayjs(item.leaveFromDate).isAfter(dayjs(leaveFromDate).subtract(1, 'day'))) return false;
    }
    if (leaveToDate) {
      if (!dayjs(item.leaveToDate).isBefore(dayjs(leaveToDate).add(1, 'day'))) return false;
    }
    if (selectedLeaveType) {
      if (item.leaveType !== selectedLeaveType) return false;
    }
    if (filterUser.trim()) {
      const uname = String(user?.username || "").toLowerCase();
      if (!uname.includes(filterUser.toLowerCase())) return false;
    }
    return true;
  });

  const applyFilters = () => { setPage(0); };

  const clearFilters = () => {
    setleaveFromDate("");
    setleaveToDate("");
    setSelectedLeaveType("");
    setSearchQuery("");
    setFilterUser("");
    setPage(0);
  };

  const handleExport = async () => {
    const rows = filteredRows || [];
    const headers = ["User", "From Date", "To Date", "Leave Type", "Leave Reason"];
    const tableRows = rows.map((r) => [
      user?.username,
      dayjs(r.leaveFromDate).format("YYYY-MM-DD"),
      dayjs(r.leaveToDate).format("YYYY-MM-DD"),
      r.leaveType,
      r.leaveReason,
    ]);

    await downloadStyledTableExcel({
      filename: `leaves_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Leaves",
      title: "Leaves",
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
  };

  const closePopup = () => {
    setPopupVisible(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Grid container>
      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={async () => await fetchInvoice()}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

      {showFilters && (
      <Grid container spacing={1} sx={{ px: 1.5, pb: 1 }}>
        <Grid item>
          <TextField label="User" size="small" value={filterUser} onChange={(e)=>setFilterUser(e.target.value)} />
        </Grid>
        <Grid item>
          <FormControl size="small" variant="outlined" sx={{ minWidth: 120 }}>
            <InputLabel>Leave Type</InputLabel>
            <Select
              value={selectedLeaveType}
              onChange={(e) => setSelectedLeaveType(e.target.value)}
              label="Leave Type"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Casual Leave (CL)">Casual Leave (CL)</MenuItem>
              <MenuItem value="Sick Leave (SL)">Sick Leave (SL)</MenuItem>
              <MenuItem value="Earned Leave / Privilege Leave (EL/PL)">Earned Leave / Privilege Leave (EL/PL)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <TextField
            size="small"
            type="date"
            label="From"
            variant="outlined"
            value={leaveFromDate}
            onChange={(e) => setleaveFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item>
          <TextField
            size="small"
            type="date"
            label="To"
            variant="outlined"
            value={leaveToDate}
            onChange={(e) => setleaveToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item>
          <Tooltip title="Clear filters">
            <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
              <ClearAllIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      )}

      {/* Table */}
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell key={column.id}>
                    {column.label}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 || fetching.invoice_list ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    {fetching.invoice_list ? (
                      <CircularProgress size={40} />
                    ) : (
                      <Alert>No Leaves</Alert>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow hover key={row.attendanceId}>
                      {columns.map((column) => {
                        const value = row[column.id];
                        if (column.id === "user") {
                          // Display the username
                          return (
                            <TableCell key={column.id}>
                              {user?.username} {/* Display the logged-in user's username */}
                            </TableCell>
                          );
                        }
                        if (column.id === "leaveFromDate" || column.id === "leaveToDate") {
                          return (
                            <TableCell key={column.id}>
                              {dayjs(value).format("YYYY-MM-DD")}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={column.id}>
                            {value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      {/* {isPopupVisible && <Popup message={responseMessage} onClose={closePopup} />} */}
      
      <AddNewInvoice
        open={open_invoice_modal}
        onClose={async () => {
          setOpenInvoiceModal(false);
          await fetchInvoice();
          setInvoiceInfo({});
        }}
        invoice_info={invoice_info}
      />
      
    </Grid>
  );
}
